import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, makeAutoObservable } from 'mobx';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { useAppStateContext } from 'src/AppState/AppState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { accountDataType } from 'src_server/types/accountData';
import addError from 'src/utils/error.jsx';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type AccountStatusType = Exclude<accountDataType.AccountDataSchemaType['accountData']['status'], undefined>;

const ACCOUNT_STATUS: Record<AccountStatusType, string> = {
    active: 'Active',
    pending: 'Pending',
    suspended: 'Suspended',
    blocked: 'Blocked',
    closed: 'Closed',
};

const ACCOUNT_STATUS_ARRAY = (): Array<FilterOptionType<AccountStatusType>> => {
    const out: Array<FilterOptionType<AccountStatusType>> = [];

    for (const [key, value] of Object.entries(ACCOUNT_STATUS)) {
        out.push({
            value: key,
            label: value,
        });
    }

    return out;
};

const CLOSURE_REASON_ARRAY = (): FilterOptionType<string>[] =>
    Array.from({ length: 25 }, (_, index) => {
        return { value: (index + 1).toString(), label: (index + 1).toString() };
    });

// guard AccountStatusType
function isAccountStatusType(status: string): status is AccountStatusType {
    return Object.keys(ACCOUNT_STATUS).includes(status);
}

export type PatchAccountsFormType = {
    status: undefined | string;
    currency: undefined | string;
    closureReasonId: null | number;
};

class CustomerSettingsState {
    public accountStatus: FormInputState<AccountStatusType, string>;
    public closureReasonId: FormInputState<string, number | null>;
    public currency: FormInputState<string /*CurrencyType*/, string>;
    private readonly form: FormModel<PatchAccountsFormType>;

    private readonly noEmptyValidationMessage = 'This field is required.';

    public constructor(
        private readonly models: ModelsState,
        private readonly accountId: () => number,
        private readonly trpcClient: TrpcClient,
        private readonly currencies: () => CurrencyType[],
        private readonly closureCodes: () => boolean
    ) {
        this.accountStatus = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.closureReasonId = FormInputState.new('').map(this.validateClosureReasonId);
        this.currency = FormInputState.new('');

        this.applyInitData();
        this.form = FormModel.group({
            status: this.accountStatus,
            currency: this.currency, //currency do not change directly!
            closureReasonId: this.closureReasonId,
        });
        makeAutoObservable(this);
    }

    public userData = (): StaffAccountBasicDataType | null => {
        const response = this.models.getUserModel(this.accountId()).basicData.data;
        if (response === null) {
            console.error('user data is null');
            return null;
        }

        return response;
    };

    public applyInitData = (): null | void => {
        const userData = this.userData();
        if (userData === null) {
            return null;
        }

        this.accountStatus.setValue(isAccountStatusType(userData.status) ? userData.status : '');
        this.closureReasonId.setValue(
            typeof userData.closureReasonId === 'number' ? userData.closureReasonId.toString() : ''
        );
        this.currency.setValue(userData.currency);
    };

    public CURRENCIES_ARRAY = (): Array<FilterOptionType</*CurrencyType*/ string>> => {
        return this.currencies().map((key) => ({ value: key.toString(), label: key }));
    };

    private createErrorLabel = (errorMessages: string[]): void => {
        errorMessages.map((error) => {
            addError(error);
        });
    };

    private validateClosureReasonId = (value: string): Result<number | null> => {
        if (value === '' && this.accountStatus.value === 'closed' && this.closureCodes()) {
            this.createErrorLabel(['User not updated: select Closure reason']);
            return Result.createError('User not updated: select Closure reason');
        } else if (value === '') return Result.createOk(null);
        const valueNumber = parseFloat(value);

        return isNaN(valueNumber) ? Result.createError('validation error') : Result.createOk(valueNumber);
    };

    private isCurrencyChangePossibleUpdate = async (): Promise<boolean> => {
        if (this.userData()?.currency !== this.currency.value) {
            const referralValidation = await this.trpcClient.client.newTrading.referralValidation.query({
                accountId: this.accountId(),
            });

            const currencyValidation = await this.trpcClient.client.walletRouter.currencyValidation.query({
                accountId: this.accountId(),
            });

            if (currencyValidation.responseStatus === 'error') {
                this.createErrorLabel(currencyValidation.data.errors.map((error) => error.message));
                return false;
            }
            if (referralValidation.responseStatus === 'success') {
                this.createErrorLabel(['Currency canot be changed when there are betslip referrals']);
                return false;
            }
        }
        return true;
    };

    @action public handleSubmit = async (): Promise<void> => {
        try {
            if (this.form.result.value.type === 'error') {
                this.createErrorLabel(['Error: validation form model failed']);
                return;
            }

            if ((await this.isCurrencyChangePossibleUpdate()) === false) {
                console.error('Not allowed to update');
                return;
            }

            console.log('form validation: ', this.form.result.value);

            const response = await this.trpcClient.client.accountData.patchAccountData.mutate({
                accountId: this.accountId(),
                accountData: this.form.result.value.data,
            });

            if (response.responseStatus === 'errorUnprocessable') {
                response.data.errors.map((err) => {
                    this.createErrorLabel([err.debugDetails]);
                });
            }
        } catch (e) {
            console.error('Unknown error occurred', e);
        }
    };
}
interface PropsType {
    id: number;
}
export const CustomerSettingsTabNew = observer('CustomerSettingsTabNew', ({ id }: PropsType): JSX.Element => {
    const {
        models,
        trpcClient,
        configState: {
            config: { currencies, closureCodes },
        },
    } = useAppStateContext();

    const [state] = useState(
        () =>
            new CustomerSettingsState(
                models,
                () => id,
                trpcClient,
                () => currencies,
                () => closureCodes
            )
    );

    return (
        <div className='customer-settings-form'>
            <div className='row'>
                <div className='col-4'>
                    <h2>Accounting</h2>
                    <CustomDropdown
                        optionsList={ACCOUNT_STATUS_ARRAY}
                        selectedValue={() => state.accountStatus.value}
                        onChangeCallback={state.accountStatus.setValue}
                        // dataTest='self-exclusion-dropdown'
                        labelHeader='Account Status'
                    />
                    {closureCodes && state.accountStatus.value === 'closed' ? (
                        <CustomDropdown
                            optionsList={CLOSURE_REASON_ARRAY}
                            selectedValue={() => state.closureReasonId.value} //TODO
                            onChangeCallback={state.closureReasonId.setValue} //TODO
                            // dataTest='self-exclusion-dropdown'
                            labelHeader='Account Status'
                        />
                    ) : null}
                    <CustomDropdown
                        optionsList={state.CURRENCIES_ARRAY}
                        selectedValue={() => state.currency.value}
                        onChangeCallback={state.currency.setValue}
                        // dataTest='self-exclusion-dropdown'
                        labelHeader='Currency'
                    />
                </div>

                <div className='col-4'>
                    <h2>Trading</h2>
                </div>
                <div className='col-4'>
                    <h2>Security</h2>
                </div>
            </div>
            <button
                data-test='submit-button'
                className='btn'
                type='submit'
                onClick={state.handleSubmit}
            >
                Submit
            </button>
        </div>
    );
});
